<template>
  <data-table-wrapper
    :filter-config="filterConfig"
    :default-sort="{ by: 'duration.start.timestamp', desc: false }"
    :default-items-per-page="25"
    @filters-changed="onFiltersChanged"
  >
    <template #default="{ tableProps, tableEvents, filters, updateFilter }">
      <div>
        <v-data-table
          v-bind="tableProps"
          v-on="tableEvents"
          :headers="headers"
          :items="filteredReports"
          item-key="meta.id"
          group-by="groupBy"
          class="elevation-2"
          :class="{ 'selection-mode': isSelectionMode }"
          max-height="800px auto"
          footer-props="footerProps"
          no-data-text="Keine Berichte vorhanden"
          locale="de"
          :item-class="getRowClass"
          @click:row="handleRowClick"
        >
          <template v-slot:top>
            <v-card flat>
              <v-card-subtitle>
                <v-row>
                  <v-col v-if="showAllReports">
                    <span class="mr-4">Filter nach Berichtsverantwortung</span>
                    <v-chip-group
                      :value="filters.assignedTo"
                      @change="updateFilter('assignedTo', $event)"
                      mandatory
                      active-class="primary--text"
                    >
                      <v-chip
                        label
                        value="all"
                        class="transparent-background font-weight-medium mr-2"
                      >
                        Alle
                      </v-chip>
                      <v-chip
                        label
                        value="own"
                        class="transparent-background font-weight-medium"
                      >
                        Eigene
                      </v-chip>
                    </v-chip-group>
                  </v-col>
                  <v-col>
                    <span class="mr-4">Filter nach Status</span>
                    <v-chip-group
                      :value="filters.status"
                      @change="updateFilter('status', $event)"
                      mandatory
                      active-class="primary--text"
                    >
                      <v-chip
                        label
                        value="all"
                        class="transparent-background font-weight-medium mr-2"
                      >
                        Alle
                      </v-chip>
                      <v-chip
                        label
                        value="inProgress"
                        class="transparent-background font-weight-medium mr-2"
                      >
                        In Bearbeitung
                      </v-chip>
                      <v-chip
                        label
                        value="toBeReviewed"
                        class="transparent-background font-weight-medium mr-2"
                      >
                        Zu prüfen
                      </v-chip>
                      <v-chip
                        label
                        value="inCorrection"
                        class="transparent-background font-weight-medium mr-2"
                      >
                        In Korrektur
                      </v-chip>
                      <v-chip
                        v-if="isArchive"
                        label
                        value="approved"
                        class="transparent-background font-weight-medium mr-2"
                      >
                        Genehmigt
                      </v-chip>
                      <v-chip
                        v-if="isArchive"
                        label
                        value="rejected"
                        class="transparent-background font-weight-medium mr-2"
                      >
                        Abgelehnt
                      </v-chip>
                      <v-chip
                        v-if="isArchive"
                        label
                        value="completed"
                        class="transparent-background font-weight-medium mr-2"
                      >
                        Festgeschrieben
                      </v-chip>
                      <v-chip
                        v-if="isArchive"
                        label
                        value="archived"
                        class="transparent-background font-weight-medium mr-2"
                      >
                        Archiviert
                      </v-chip>
                    </v-chip-group>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <span class="mr-4">Gruppen</span>
                    <v-btn
                      small
                      depressed
                      class="mr-2"
                      @click="expandAllGroups"
                      :disabled="!hasClosedGroups"
                    >
                      <v-icon small left>mdi-chevron-down</v-icon>
                      Alle ausklappen
                    </v-btn>
                    <v-btn
                      small
                      depressed
                      @click="collapseAllGroups"
                      :disabled="!hasOpenGroups"
                    >
                      <v-icon small left>mdi-chevron-up</v-icon>
                      Alle einklappen
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-subtitle>
            </v-card>
            <v-divider></v-divider>
          </template>

          <!-- All your existing item templates -->
          <template #[`item.title`]="{ item }">
            <span :class="!item.title ? 'grey--text' : ''">
              {{ item.title ? item.title : "Nicht festgelegt" }}
            </span>
          </template>

          <template #[`item.assignedTo.uid`]="{ item }">
            <v-chip small>
              <v-icon small left>mdi-account-circle</v-icon>
              {{ item.assignedTo.displayName }}
            </v-chip>
          </template>

          <template #[`item.type.title`]="{ item }">
            <duty-type-chip :dutyType="item.type" small />
          </template>

          <template #[`item.duration.start.timestamp`]="{ item }">
            <span :class="!item.duration.start.timestamp ? 'grey--text' : ''">
              {{
                item.duration.start.timestamp
                  ? timestampConverter(item.duration.start.timestamp)
                  : "Nicht festgelegt"
              }}
            </span>
          </template>

          <template #[`item.duration.end.timestamp`]="{ item }">
            <span :class="!item.duration.end.timestamp ? 'grey--text' : ''">
              <template
                v-if="
                  !item.duration.end.timestamp &&
                  item.duration.end.estimatedTimestamp
                "
              >
                Erwartet:<br />
                {{ timestampConverter(item.duration.end.estimatedTimestamp) }}
              </template>
              <template v-else>
                {{
                  item.duration.end.timestamp
                    ? timestampConverter(item.duration.end.timestamp)
                    : "Nicht festgelegt"
                }}
              </template>
            </span>
          </template>

          <template #[`item.status`]="{ item }">
            <report-status-chip :status="item.status" small />
          </template>

          <template #[`item.actions`]="{ item }">
            <v-btn
              v-if="showActionBtn"
              depressed
              @click="openDutyReport(item, 'newTab')"
              small
            >
              <v-icon left small>mdi-open-in-app</v-icon>
              Öffnen
            </v-btn>
            <v-menu v-else offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon text v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list nav>
                <v-list-item link @click="routeDetails(item)">
                  <v-list-item-icon>
                    <v-icon dense>mdi-open-in-app</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Bericht öffnen</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item link @click="openDutyReport(item, 'newTab')">
                  <v-list-item-icon>
                    <v-icon dense>mdi-open-in-new</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    Bericht öffnen (in neuem Tab)
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <template v-slot:group.header="{ items, isOpen, toggle }">
            <th :colspan="headers.length">
              <v-btn
                @click="
                  () => {
                    toggle();
                    updateGroupState();
                  }
                "
                small
                icon
                :ref="items[0].groupBy"
                :data-open="isOpen"
                class="mr-1"
              >
                <v-icon>
                  {{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
              </v-btn>
              {{ stripPrefix(items[0].groupBy) }}
              <span class="font-weight-regular grey--text">
                ({{ getCountOfReportsInMonth(items[0].groupBy) }})
              </span>
            </th>
          </template>
        </v-data-table>
      </div>
    </template>
  </data-table-wrapper>
</template>

<script>
import { auth } from "@/firebase";
import DutyTypeChip from "@/components/duty-management/DutyTypeChip.vue";
import ReportStatusChip from "@/components/duty-management/ReportStatusChip.vue";
import DataTableWrapper from "@/components/_system/DataTableWrapper.vue";
import { DUTY_MANAGEMENT_REPORTS_read_all } from "@/data/permission-types.js";

export default {
  name: "reports-data-table",
  components: {
    DutyTypeChip,
    ReportStatusChip,
    DataTableWrapper,
  },
  props: {
    reports: {
      type: Array,
      required: true,
    },
    isArchive: {
      type: Boolean,
      required: false,
      default: false,
    },
    showActionBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Selection mode: 'navigate' (default) routes to detail page, 'select' emits @select event
    selectionMode: {
      type: String,
      default: "navigate",
      validator: (value) => ["navigate", "select"].includes(value),
    },
    // Currently selected report ID (for visual highlighting in selection mode)
    selectedId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      currentUserUid: auth.currentUser.uid,
      // Simple state tracking - no complex reactivity
      allGroupsExpanded: true,
      hasClosedGroups: false,
      hasOpenGroups: true,
      // Configuration for the URL sync wrapper
      filterConfig: {
        status: {
          default: "all",
          urlKey: "status",
        },
        assignedTo: {
          default: "own",
          urlKey: "assignedTo",
        },
      },
      // Current filter values (will be updated by wrapper)
      currentFilters: {
        status: "all",
        assignedTo: "own",
      },
      footerProps: {
        "items-per-page-options": [25, 50, 100, 150, 200, -1],
      },
    };
  },
  computed: {
    reportsInput() {
      return this.reports.map((report) => ({
        ...report,
        groupBy: this.groupByDateText(report.duration.start.timestamp.toDate()),
      }));
    },

    filteredReports() {
      return this.reportsInput.filter((report) => {
        // Apply status filter
        if (
          this.currentFilters.status !== "all" &&
          report.status !== this.currentFilters.status
        ) {
          return false;
        }

        // Apply assignedTo filter
        if (this.currentFilters.assignedTo === "own") {
          return report.assignedTo.uid === this.currentUserUid;
        }

        return true;
      });
    },

    showAllReports() {
      return this.$store.getters["organization/checkPermissionByID"](
        `${DUTY_MANAGEMENT_REPORTS_read_all}`
      );
    },

    // Returns true when in selection mode (for split-view)
    isSelectionMode() {
      return this.selectionMode === "select";
    },

    headers() {
      return [
        { text: "Titel", value: "title" },
        { text: "Dienstart", value: "type.title" },
        { text: "Startzeitpunkt", value: "duration.start.timestamp" },
        { text: "Endzeitpunkt", value: "duration.end.timestamp" },
        {
          text: "Berichtsverantwortlich",
          value: "assignedTo.displayName",
        },
        { text: "Status", value: "status" },
        { value: "actions", sortable: false },
      ];
    },
  },
  watch: {
    filteredReports() {
      // Reset state when filtered data changes
      this.$nextTick(() => {
        this.resetGroupState();
      });
    },
  },
  mounted() {
    // Initialize state after component is mounted
    this.$nextTick(() => {
      this.resetGroupState();
    });
  },
  updated() {
    // Reset state when component updates (pagination, filtering, etc.)
    this.$nextTick(() => {
      this.resetGroupState();
    });
  },
  methods: {
    onFiltersChanged(filters) {
      this.currentFilters = { ...filters };
    },

    /**
     * Returns CSS class for table row based on selection state
     */
    getRowClass(item) {
      if (this.isSelectionMode && item.meta?.id === this.selectedId) {
        return "selected-row";
      }
      return "";
    },

    /**
     * Handle row click based on selection mode
     */
    handleRowClick(item) {
      if (this.isSelectionMode) {
        // In selection mode, emit select event with report ID
        this.$emit("select", item.meta?.id);
      } else {
        // In navigate mode, route to details page
        this.routeDetails(item);
      }
    },

    expandAllGroups() {
      // Simple approach: click all closed groups
      Object.keys(this.$refs).forEach((key) => {
        const ref = this.$refs[key];
        if (ref && ref.$attrs && !ref.$attrs["data-open"]) {
          ref.$el.click();
        }
      });
      // Update states
      this.allGroupsExpanded = true;
      this.hasClosedGroups = false;
      this.hasOpenGroups = true;
    },

    collapseAllGroups() {
      // Simple approach: click all open groups
      Object.keys(this.$refs).forEach((key) => {
        const ref = this.$refs[key];
        if (ref && ref.$attrs && ref.$attrs["data-open"]) {
          ref.$el.click();
        }
      });
      // Update states
      this.allGroupsExpanded = false;
      this.hasClosedGroups = true;
      this.hasOpenGroups = false;
    },

    onGroupToggle() {
      // Called when individual groups are toggled
      // Check current state and update flags - ONLY for visible groups
      this.$nextTick(() => {
        const refs = Object.keys(this.$refs);
        if (refs.length === 0) {
          // No visible groups - disable both buttons
          this.hasOpenGroups = false;
          this.hasClosedGroups = false;
          this.allGroupsExpanded = true;
          return;
        }

        const openGroups = [];
        const closedGroups = [];

        // Only check refs that actually exist (visible groups)
        refs.forEach((key) => {
          const ref = this.$refs[key];
          if (ref && ref.$attrs) {
            if (ref.$attrs["data-open"]) {
              openGroups.push(key);
            } else {
              closedGroups.push(key);
            }
          }
        });

        // Update state flags based ONLY on visible groups
        this.hasOpenGroups = openGroups.length > 0;
        this.hasClosedGroups = closedGroups.length > 0;
        this.allGroupsExpanded =
          closedGroups.length === 0 && openGroups.length > 0;
      });
    },

    updateGroupState() {
      // Simplified - just trigger state check
      this.onGroupToggle();
    },

    resetGroupState() {
      // Reset to default state - useful when pagination/filters change
      this.$nextTick(() => {
        // Check the current visible groups state
        this.onGroupToggle();
      });
    },

    routeDetails(item) {
      if (!this.showActionBtn) {
        this.openDutyReport(item);
      }
    },

    openDutyReport(item, type = "default") {
      const routeName =
        item.status === "inProgress"
          ? "duty-management-reports-live"
          : "duty-management-reports-details";

      const route = {
        name: routeName,
        params: {
          organizationId: this.$route.params.organizationId,
          itemId: item.meta.id,
        },
      };

      if (type === "newTab") {
        window.open(this.$router.resolve(route).href, "_blank");
      } else {
        this.$router.push(route);
      }
    },

    groupByDateText(date) {
      const month = ("0" + (date.getMonth() + 1)).slice(-2);
      const year = date.getFullYear();
      const monthLong = date.toLocaleString("de-DE", { month: "long" });
      return year + "-" + month + "_" + monthLong + " " + year;
    },

    stripPrefix(item) {
      return item.substring(item.indexOf("_") + 1);
    },

    getCountOfReportsInMonth(monthId) {
      return this.filteredReports.filter((report) => report.groupBy === monthId)
        .length;
    },

    timestampConverter(timestamp) {
      const options = {
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },
  },
};
</script>

<style scoped>
/* Selected row highlighting for selection mode */
:deep(.selected-row) {
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
}

:deep(.selection-mode .v-data-table__wrapper tbody tr:hover) {
  cursor: pointer;
}

/* Ensure selected row stays highlighted even on hover */
:deep(.selection-mode .selected-row:hover) {
  background-color: rgba(var(--v-theme-primary), 0.16) !important;
}

/* Dark mode support */
.theme--dark :deep(.selected-row) {
  background-color: rgba(25, 118, 210, 0.24) !important;
}

.theme--dark :deep(.selection-mode .selected-row:hover) {
  background-color: rgba(25, 118, 210, 0.32) !important;
}
</style>
